import React, { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Flame, Users, Send, ExternalLink, FileDown, CheckCircle2, XCircle, Eye, Search, ClipboardCheck, MessageCircle, Upload, FileSpreadsheet, BarChart3, RefreshCw, Link as LinkIcon, Copy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const STATUS_STYLE = {
  pendente: "bg-cream-400 text-forest-900",
  convidado: "bg-copaiba-500 text-forest-900",
  acessou: "bg-copaiba-300 text-forest-900",
  concluido: "bg-forest-700 text-cream-100",
};
const STATUS_LABEL = { pendente: "Pendente", convidado: "Convidado", acessou: "Acessou link", concluido: "Inscrição concluída" };
const DECISION_LABEL = { pendente: "—", aprovado: "Aprovado", indeferido: "Indeferido" };

export default function BrigadistasManager() {
  const [processes, setProcesses] = useState([]);
  const [pid, setPid] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [sendQueueOpen, setSendQueueOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadProcesses = () => api.get("/secretariat/pss/processes").then((r) => {
    setProcesses(r.data);
    if (r.data.length && !pid) setPid(r.data[0].id);
  }).catch((e) => toast.error(formatApiError(e)));

  const loadCandidates = () => {
    if (!pid) return;
    api.get(`/secretariat/pss/processes/${pid}/candidates`).then((r) => setCandidates(r.data)).catch(() => {});
    api.get(`/secretariat/pss/processes/${pid}/summary`).then((r) => setSummary(r.data)).catch(() => {});
  };

  useEffect(() => { loadProcesses(); }, []);
  useEffect(() => { loadCandidates(); }, [pid]);

  const filtered = useMemo(() => {
    let arr = candidates;
    if (statusFilter) arr = arr.filter((c) => c.status === statusFilter);
    if (courseFilter) {
      arr = arr.filter((c) => {
        if (c.decision !== "aprovado") return false;
        if (courseFilter === "sem_convite") return !c.course_status || c.course_status === "nao_convocado";
        return c.course_status === courseFilter;
      });
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.community || "").toLowerCase().includes(s) || (c.phone || "").includes(s.replace(/\D/g, "")));
    }
    return arr;
  }, [candidates, q, statusFilter, courseFilter]);

  const process = processes.find((p) => p.id === pid);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(new Set(filtered.map((c) => c.id)).size === selected.size ? new Set() : new Set(filtered.map((c) => c.id)));

  const decide = async (cid, decision) => {
    const note = decision === "indeferido" ? (window.prompt("Motivo da indeferimento (opcional):") || "") : "";
    try { await api.patch(`/secretariat/pss/candidates/${cid}/decision`, { decision, decision_note: note }); toast.success("Decisão atualizada."); loadCandidates(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (cid) => {
    if (!window.confirm("Remover este candidato?")) return;
    try { await api.delete(`/secretariat/pss/candidates/${cid}`); toast.success("Removido."); loadCandidates(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const renew = async (c) => {
    const msg = c.status === "concluido"
      ? "Renovar o link deste candidato apagará TODOS os documentos enviados, o protocolo e o aceite do edital. O candidato poderá refazer a inscrição do zero.\n\nDeseja continuar?"
      : "Renovar o link deste candidato irá gerar um novo link único para envio de documentos. O candidato poderá recomeçar a inscrição.\n\nDeseja continuar?";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/secretariat/pss/candidates/${c.id}/renew`);
      const newToken = r.data.invite_token;
      const link = `${window.location.origin}/pss/inscricao/${newToken}`;
      try { await navigator.clipboard.writeText(link); toast.success("Link renovado e copiado!"); }
      catch { toast.success("Link renovado. Copie no detalhe do candidato."); }
      loadCandidates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copySelfRegisterLink = async () => {
    if (!process?.self_register_enabled) {
      toast.error("Este processo não permite auto-cadastro público. Habilite em 'Editar processo'.");
      return;
    }
    const link = `${window.location.origin}/pss/auto-cadastro/${process.code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link de auto-cadastro copiado! Compartilhe com os presidentes das comunidades.");
    } catch {
      window.prompt("Copie o link:", link);
    }
  };

  const downloadCourseReport = async () => {
    try {
      const r = await api.get(`/secretariat/pss/course/report?pss_id=${pid}&status=${courseFilter || ""}`);
      const { items, course, count } = r.data;
      const statusLbl = {
        confirmado: "Confirmaram participação",
        aguardando: "Aguardando confirmação",
        nao_participara: "Não participarão do curso",
        sem_convite: "Aprovados sem convocação enviada",
      }[courseFilter] || "Aprovados (todos os status do curso)";
      const rows = [
        ["Relatório — Convocação para o Curso de Formação"],
        [`${course?.titulo || "Curso de Formação de Brigada COOMFLONA"}`],
        [`Período: ${course?.periodo} · Local: ${course?.local}`],
        [`Filtro aplicado: ${statusLbl} · Total: ${count}`],
        ["AVISO: Este relatório lista apenas candidatos selecionados para o CURSO. Não representa contratação ou ingresso definitivo na Brigada."],
        [""],
        ["#", "Nome", "Comunidade", "Telefone", "CPF", "Protocolo", "Situação do curso", "Convocado em", "Respondido em"],
        ...items.map((it, i) => [
          i + 1, it.name || "", it.community || "", it.phone_display || "", it.cpf || "",
          it.protocol || "", it.course_status || "sem_convite",
          it.course_invited_at ? new Date(it.course_invited_at).toLocaleString("pt-BR") : "",
          it.course_responded_at ? new Date(it.course_responded_at).toLocaleString("pt-BR") : "",
        ]),
      ];
      const csv = rows.map((row) => row.map((v) => {
        const s = String(v ?? "");
        return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(";")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio_curso_${courseFilter || "todos"}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Relatório gerado (${count} candidatos).`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendCourseInvite = async (c) => {    try {
      const r = await api.post(`/secretariat/pss/candidates/${c.id}/course-invite`);
      const { token, course } = r.data;
      const link = `${window.location.origin}/pss/convocacao/${token}`;
      const msg = `Olá ${c.name}! 🌱

Você foi *APROVADO(A)* no ${c.process_code} para participar do *${course.titulo}*.

📅 Período: *${course.periodo}*
📍 Local: *${course.local}*

⚠️ IMPORTANTE: Aprovação neste processo significa somente que você foi selecionado para participar do curso. NÃO significa contratação ou ingresso definitivo na Brigada.

Por favor, confirme sua participação no link abaixo:
${link}

Aguardamos sua resposta! — Secretaria COOMFLONA`;
      const phone = (c.phone || "").replace(/\D/g, "");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
      try { await navigator.clipboard.writeText(link); } catch { /* noop */ }
      toast.success("Convocação gerada — WhatsApp aberto. Link também copiado.");
      loadCandidates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadProtocol = async (c) => {
    if (!c.protocol) { toast.error("Candidato ainda não concluiu a inscrição."); return; }
    const token = localStorage.getItem("coomflona_token");
    const res = await fetch(`${API}/secretariat/pss/candidates/${c.id}/protocol-pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Erro"); return; }
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `protocolo_${c.protocol}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const handleImport = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f, f.name);
    try {
      const token = localStorage.getItem("coomflona_token");
      const res = await fetch(`${API}/secretariat/pss/processes/${pid}/candidates/import-file`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Erro"); }
      const j = await res.json();
      toast.success(`Importados: ${j.created} · Ignorados: ${j.skipped} (de ${j.total_rows} linhas)`);
      loadCandidates();
    } catch (err) { toast.error(err.message); } finally { e.target.value = ""; }
  };

  const downloadReport = async (format) => {
    try {
      const token = localStorage.getItem("coomflona_token");
      const res = await fetch(`${API}/secretariat/pss/processes/${pid}/report.${format}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erro ao gerar relatório");
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `relatorio_${process?.code || 'pss'}.${format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) { toast.error(err.message); }
  };

  if (!process) return <div className="mt-6 text-forest-900/65">Carregando processo seletivo...</div>;

  const editalUrl = `${API}/pss/processes/${process.code}/pdf`;
  const selectedList = filtered.filter((c) => selected.has(c.id));

  return (
    <div className="mt-8" data-testid="brigadistas-manager">
      <div className="bg-forest-700 text-cream-100 p-5 mb-6">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Flame className="w-5 h-5 text-copaiba-300" />
          <div className="font-display text-xl">{process.title}</div>
          <span className="ml-auto text-xs font-mono bg-copaiba-500 text-forest-900 px-2 py-0.5">{process.code}</span>
        </div>
        <p className="text-cream-100/85 text-sm">
          {process.subtitle} · Prazo: <b>{process.deadline}</b>.
          {process.vagas_total ? <> Vagas: <b>{process.vagas_total}</b>.</> : null}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {processes.length > 1 && (
            <select value={pid} onChange={(e) => { setPid(e.target.value); setSelected(new Set()); }} data-testid="process-selector"
              className="bg-cream-100 text-forest-900 border border-cream-100/30 px-2 py-1.5">
              {processes.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.title}</option>)}
            </select>
          )}
          <a href={editalUrl} target="_blank" rel="noreferrer" data-testid="download-edital" className="inline-flex items-center gap-1 bg-cream-100 text-forest-900 px-3 py-1.5"><FileDown className="w-3.5 h-3.5" /> Edital PDF</a>
          <button onClick={() => setAddOpen(true)} data-testid="add-candidate-btn" className="inline-flex items-center gap-1 bg-copaiba-500 text-forest-900 px-3 py-1.5 hover:bg-copaiba-300">
            <Users className="w-3.5 h-3.5" /> + Adicionar
          </button>
          <a href={`${API}/secretariat/pss/import-template.xlsx`} target="_blank" rel="noreferrer"
             onClick={async (e) => { e.preventDefault(); const t = localStorage.getItem("coomflona_token"); const r = await fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${t}` } }); const b = await r.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "template_candidatos.xlsx"; a.click(); }}
             data-testid="download-template" className="inline-flex items-center gap-1 border border-cream-100/30 text-cream-100 px-3 py-1.5 hover:bg-forest-900">
            <FileDown className="w-3.5 h-3.5" /> Template .xlsx
          </a>
          <label className="cursor-pointer inline-flex items-center gap-1 border border-cream-100/30 text-cream-100 px-3 py-1.5 hover:bg-forest-900" data-testid="import-file-btn">
            <Upload className="w-3.5 h-3.5" /> Importar planilha
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={handleImport} />
          </label>
          <button onClick={() => downloadReport("xlsx")} data-testid="report-xlsx" className="inline-flex items-center gap-1 border border-cream-100/30 text-cream-100 px-3 py-1.5 hover:bg-forest-900">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Relatório Excel
          </button>
          <button onClick={() => downloadReport("pdf")} data-testid="report-pdf" className="inline-flex items-center gap-1 border border-cream-100/30 text-cream-100 px-3 py-1.5 hover:bg-forest-900">
            <FileDown className="w-3.5 h-3.5" /> Relatório PDF
          </button>
          {process.self_register_enabled && (
            <button onClick={copySelfRegisterLink} data-testid="copy-self-register-link"
              className="inline-flex items-center gap-1 bg-copaiba-300 text-forest-900 px-3 py-1.5 hover:bg-copaiba-500"
              title="Link público para presidentes de comunidades compartilharem com candidatos">
              <LinkIcon className="w-3.5 h-3.5" /> Link auto-cadastro
            </button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5" data-testid="pss-summary">
          <Stat label="Total" v={summary.total} bg />
          <Stat label="Convidados" v={summary.convidados} />
          <Stat label="Acessaram link" v={summary.acessaram} />
          <Stat label="Concluídos" v={summary.concluidos} />
          <Stat label="Aprovados" v={summary.aprovados} />
          <Stat label="Pendentes" v={summary.pendentes} />
        </div>
      )}

      {summary && summary.curso && summary.aprovados > 0 && (
        <div className="bg-copaiba-100 border border-copaiba-300 p-4 mb-5" data-testid="pss-course-summary">
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2 flex items-center gap-1">
            <Flame className="w-3.5 h-3.5" /> Curso de Formação de Brigada · 24-28 ago/2026 · Base COOMFLONA Km 117
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Confirmaram" v={summary.curso.confirmado} bg />
            <Stat label="Aguardando resposta" v={summary.curso.aguardando} />
            <Stat label="Não participarão" v={summary.curso.nao_participara} />
            <Stat label="Aprovados sem convite" v={summary.curso.sem_convite} />
          </div>
          <div className="text-xs text-forest-900/70 mt-2 italic">
            ⚠️ Aprovado = selecionado para o CURSO. Não significa contratação/admissão na Brigada.
          </div>
        </div>
      )}

      {summary && summary.total > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mb-5" data-testid="pss-charts">
          <div className="bg-white border border-cream-400 p-4">
            <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2 flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> Inscrições por comunidade</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(() => {
                const arr = [...(summary.by_community || [])];
                // Garante que Marituba apareça no gráfico mesmo com 0 inscrições
                if (!arr.some((x) => (x.name || "").toLowerCase() === "marituba")) {
                  arr.push({ name: "Marituba", count: 0 });
                }
                return arr.slice(0, 14);
              })()} margin={{ top: 5, right: 10, left: -18, bottom: 20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" fill="#1A3620" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-cream-400 p-4">
            <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Situação das inscrições</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={[
                  { name: "Concluídos", value: summary.concluidos, color: "#1A3620" },
                  { name: "Convidados sem resposta", value: Math.max(0, summary.convidados - summary.concluidos), color: "#B7864E" },
                  { name: "Pendentes de envio", value: Math.max(0, summary.total - summary.convidados), color: "#D9C39E" },
                  { name: "Aprovados", value: summary.aprovados, color: "#5A8A5F" },
                  { name: "Indeferidos", value: summary.indeferidos, color: "#A54A2A" },
                ].filter((x) => x.value > 0)} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}>
                  {[0, 1, 2, 3, 4].map((i) => <Cell key={i} fill={["#1A3620", "#B7864E", "#D9C39E", "#5A8A5F", "#A54A2A"][i]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white border border-cream-400 p-4 mb-4 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-700/60" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nome, comunidade, telefone..." data-testid="pss-search" className="w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none pl-10 pr-4 py-2.5 text-sm" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="pss-status-filter" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} data-testid="pss-course-filter" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          <option value="">Curso: todos</option>
          <option value="confirmado">✅ Confirmados</option>
          <option value="aguardando">⏳ Aguardando resposta</option>
          <option value="nao_participara">❌ Não participarão</option>
          <option value="sem_convite">📭 Aprovados sem convite</option>
        </select>
        <Button onClick={() => setSendQueueOpen(true)} disabled={selected.size === 0} data-testid="open-send-queue" className="bg-[#25D366] hover:opacity-90 text-white rounded-none disabled:opacity-40">
          <Send className="w-4 h-4 mr-2" /> Enviar convites ({selected.size})
        </Button>
        {courseFilter && (
          <Button onClick={downloadCourseReport} data-testid="download-course-report"
            className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
            <FileDown className="w-4 h-4 mr-2" /> Relatório do curso (PDF)
          </Button>
        )}
      </div>

      <div className="bg-white border border-cream-400">
        <div className="px-4 py-2 border-b border-cream-400 flex items-center gap-2 bg-cream-100 text-xs">
          <Checkbox
            checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
            onCheckedChange={toggleAll}
            data-testid="pss-select-all"
            className="data-[state=checked]:bg-forest-700 data-[state=checked]:border-forest-700"
          />
          <span>Selecionar todos ({filtered.length})</span>
        </div>
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-forest-900/65">Nenhum candidato.</div>
        ) : filtered.map((c) => (
          <div key={c.id} data-testid={`pss-row-${c.id}`} className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 border-b last:border-0 border-cream-400">
            <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`pss-check-${c.id}`} className="data-[state=checked]:bg-forest-700 data-[state=checked]:border-forest-700" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${STATUS_STYLE[c.status] || STATUS_STYLE.pendente}`}>{STATUS_LABEL[c.status] || c.status}</span>
                {c.decision !== "pendente" && (
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${c.decision === "aprovado" ? "bg-forest-900 text-cream-100" : "bg-terracotta-500 text-cream-100"}`}>{DECISION_LABEL[c.decision]}</span>
                )}
                {c.decision === "aprovado" && c.course_status && c.course_status !== "nao_convocado" && (
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${
                    c.course_status === "confirmado" ? "bg-forest-700 text-cream-100" :
                    c.course_status === "nao_participara" ? "bg-terracotta-500 text-cream-100" :
                    "bg-copaiba-300 text-forest-900"
                  }`} title="Status do curso">
                    Curso: {c.course_status === "confirmado" ? "✅ confirmado" : c.course_status === "nao_participara" ? "❌ não participará" : "⏳ aguardando"}
                  </span>
                )}
                {c.protocol && <span className="text-[10px] font-mono bg-cream-100 border border-cream-400 px-2 py-0.5">{c.protocol}</span>}
              </div>
              <div className="font-medium text-forest-900 mt-1">{c.name}</div>
              <div className="text-xs text-forest-900/70">{c.community || "—"} · {c.phone_display}</div>
            </div>
            <div className="flex flex-wrap gap-1 shrink-0">
              <button onClick={() => setDetail(c)} data-testid={`pss-view-${c.id}`} className="p-2 text-forest-700 hover:bg-cream-200"><Eye className="w-4 h-4" /></button>
              <button onClick={() => setEditing(c)} data-testid={`pss-edit-${c.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Editar"><span className="text-xs">✎</span></button>
              <button onClick={() => downloadProtocol(c)} data-testid={`pss-pdf-${c.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Protocolo PDF"><FileDown className="w-4 h-4" /></button>
              <button onClick={() => renew(c)} data-testid={`pss-renew-${c.id}`} className="p-2 text-copaiba-700 hover:bg-cream-200" title="Renovar link (gera novo link e permite refazer inscrição)"><RefreshCw className="w-4 h-4" /></button>
              <button onClick={() => decide(c.id, "aprovado")} data-testid={`pss-approve-${c.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Aprovar"><CheckCircle2 className="w-4 h-4" /></button>
              <button onClick={() => decide(c.id, "indeferido")} data-testid={`pss-reject-${c.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200" title="Indeferir"><XCircle className="w-4 h-4" /></button>
              {c.decision === "aprovado" && (
                <button onClick={() => sendCourseInvite(c)} data-testid={`pss-course-invite-${c.id}`}
                  className="p-2 text-[#128C7E] hover:bg-cream-200" title="Enviar convocação do curso via WhatsApp">
                  <Flame className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Send Queue Dialog */}
      <SendQueueDialog open={sendQueueOpen} onOpenChange={setSendQueueOpen} candidates={selectedList} process={process} onDone={() => { setSelected(new Set()); loadCandidates(); }} />

      {/* Detail Dialog */}
      <CandidateDetailDialog c={detail} onClose={() => setDetail(null)} onDecide={decide} onDelete={del} onDownload={downloadProtocol} onRenew={renew} />

      {/* Add/Edit Candidate */}
      <CandidateFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={() => { setAddOpen(false); loadCandidates(); }}
        processId={pid}
      />
      <CandidateFormDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        onSaved={() => { setEditing(null); loadCandidates(); }}
        processId={pid}
        editing={editing}
      />
    </div>
  );
}

function Stat({ label, v, bg }) {
  return (
    <div className={`p-3 border ${bg ? "bg-forest-700 border-forest-700 text-cream-100" : "bg-white border-cream-400"}`}>
      <div className={`font-display text-2xl ${bg ? "text-copaiba-300" : "text-forest-900"}`}>{v ?? 0}</div>
      <div className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${bg ? "text-cream-100/80" : "text-forest-900/65"}`}>{label}</div>
    </div>
  );
}

function SendQueueDialog({ open, onOpenChange, candidates, process, onDone }) {
  const [sentIds, setSentIds] = useState(new Set());
  useEffect(() => { if (open) setSentIds(new Set()); }, [open]);

  const buildLink = (c) => {
    const base = window.location.origin;
    const link = `${base}/pss/inscricao/${c.invite_token}`;
    const tpl = process.whatsapp_message_template || "Olá {nome}, acesse o link: {link}";
    const msg = tpl.replace(/\{nome\}/g, c.name.split(" ")[0]).replace(/\{link\}/g, link);
    return { link, waUrl: `https://wa.me/${c.phone}?text=${encodeURIComponent(msg)}` };
  };

  const openAndMark = (c) => {
    const { waUrl } = buildLink(c);
    window.open(waUrl, "_blank");
    setSentIds((s) => new Set([...s, c.id]));
  };

  const confirmAllSent = async () => {
    const ids = [...sentIds];
    if (ids.length === 0) { toast.error("Nenhuma conversa foi aberta. Clique em 'Abrir WhatsApp' antes."); return; }
    try {
      await api.post("/secretariat/pss/candidates/mark-sent", { ids });
      toast.success(`${ids.length} candidato(s) marcado(s) como convidado(s).`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Fila de envio — WhatsApp</DialogTitle>
          <p className="text-sm text-forest-900/70 mt-1">
            <b>{candidates.length}</b> candidato(s) selecionado(s). Para cada um: clique em <b>&quot;Abrir WhatsApp&quot;</b>,
            aperte <b>ENVIAR</b> lá no WhatsApp, volte aqui e clique no próximo.
            Ao final, clique em <b>&quot;Confirmar todos como enviados&quot;</b>.
          </p>
        </DialogHeader>
        <div className="bg-copaiba-100 border border-copaiba-300 p-3 text-xs text-forest-900 mb-3">
          <b>Dica:</b> Se o navegador bloquear a abertura de uma janela nova, autorize os popups deste site.
          Um botão por vez, sem pressa — assim o WhatsApp abre corretamente cada conversa.
        </div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {candidates.map((c, idx) => {
            const done = sentIds.has(c.id);
            return (
              <div key={c.id} className={`flex items-center gap-3 p-2 border ${done ? "bg-forest-700/5 border-forest-700/40" : "bg-white border-cream-400"}`} data-testid={`queue-row-${c.id}`}>
                <div className="text-xs text-forest-900/55 w-6 text-center">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-forest-900 truncate">{c.name}</div>
                  <div className="text-xs text-forest-900/65">{c.community} · {c.phone_display}</div>
                </div>
                <Button onClick={() => openAndMark(c)} data-testid={`queue-open-${c.id}`}
                  className={`rounded-none text-xs h-8 ${done ? "bg-forest-700 hover:bg-forest-900 text-cream-100" : "bg-[#25D366] hover:opacity-90 text-white"}`}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  {done ? "Aberto ✓" : "Abrir WhatsApp"}
                </Button>
              </div>
            );
          })}
        </div>
        <DialogFooter className="mt-4">
          <div className="text-sm text-forest-900/70 mr-auto">Abertos: <b>{sentIds.size}</b> / {candidates.length}</div>
          <Button onClick={confirmAllSent} disabled={sentIds.size === 0} data-testid="confirm-all-sent" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none disabled:opacity-40">
            <ClipboardCheck className="w-4 h-4 mr-2" /> Confirmar todos como enviados ({sentIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateDetailDialog({ c, onClose, onDecide, onDelete, onDownload, onRenew }) {
  const [blobs, setBlobs] = useState({});
  const [errors, setErrors] = useState({});
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (!c) { setBlobs({}); setErrors({}); setHealth(null); return; }
    // health diagnostic (best-effort)
    api.get(`/secretariat/pss/candidates/${c.id}/file-health`)
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null));
    const filesToLoad = [];
    if (c.cv_url) filesToLoad.push({ key: "cv", filename: c.cv_url.split("/").pop() });
    if (c.selfie_url) filesToLoad.push({ key: "selfie", filename: c.selfie_url.split("/").pop() });
    (c.docs_urls || []).forEach((d, i) => filesToLoad.push({ key: `doc-${i}`, filename: d.stored_name }));
    if (filesToLoad.length === 0) { setBlobs({}); setErrors({}); return; }
    const controller = new AbortController();
    const token = localStorage.getItem("coomflona_token");
    const created = {};
    const errs = {};
    let cancelled = false;
    Promise.all(filesToLoad.map(async (f) => {
      try {
        const res = await fetch(`${API}/secretariat/pss/candidate-file/${f.filename}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) { errs[f.key] = `HTTP ${res.status}`; return null; }
        const blob = await res.blob();
        if (blob.size === 0) { errs[f.key] = "arquivo vazio"; return null; }
        const url = URL.createObjectURL(blob);
        created[f.key] = { url, mime: blob.type, size: blob.size };
        return null;
      } catch (e) {
        if (e.name !== "AbortError") errs[f.key] = e.message || "falha ao carregar";
        return null;
      }
    })).then(() => {
      if (!cancelled) { setBlobs({ ...created }); setErrors({ ...errs }); }
    });
    return () => {
      cancelled = true;
      controller.abort();
      Object.values(created).forEach((v) => v && URL.revokeObjectURL(v.url));
    };
  }, [c?.id]);

  if (!c) return null;
  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{c.name}</DialogTitle>
          <p className="text-sm text-forest-900/70 mt-1">{c.community || "—"} · {c.phone_display}</p>
        </DialogHeader>

        {health && health.all_available === false && (
          <div className="bg-terracotta-500/10 border-2 border-terracotta-500 p-4 mb-4" data-testid="detail-recovery-card">
            <div className="font-medium text-terracotta-500 mb-2">⚠️ Alguns arquivos deste candidato estão indisponíveis no servidor</div>
            <div className="text-sm text-forest-900 mb-3">
              Isso pode ter acontecido por uma manutenção no servidor. Os arquivos que <b>ainda estão</b> disponíveis aparecem abaixo. Para recuperar os arquivos perdidos, <b>renove o link</b> deste candidato e peça para ele reenviar tudo pelo WhatsApp — a partir de agora os novos uploads ficam salvos permanentemente.
            </div>
            <ul className="text-xs mb-3 text-forest-900/80 space-y-0.5">
              {health.cv && <li>Currículo: {health.cv.available ? "✅ disponível" : "❌ perdido"}</li>}
              {health.selfie && <li>Selfie: {health.selfie.available ? "✅ disponível" : "❌ perdido"}</li>}
              {(health.docs || []).map((d, i) => <li key={i}>Documento {d.original_name || i}: {d.available ? "✅ disponível" : "❌ perdido"}</li>)}
            </ul>
            <Button onClick={() => { onRenew(c); }} data-testid="detail-renew-btn"
              className="rounded-none bg-terracotta-500 hover:bg-terracotta-500/80 text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Renovar link e copiar (para reenviar ao candidato)
            </Button>
          </div>
        )}
        <div className="space-y-2 text-sm">
          <Row l="Status">{STATUS_LABEL[c.status] || c.status} {c.decision !== "pendente" && `· Decisão: ${DECISION_LABEL[c.decision]}`}</Row>
          <Row l="CPF">{c.cpf ? c.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"}</Row>
          <Row l="RG">{c.rg || "—"}</Row>
          <Row l="E-mail">{c.email || "—"}</Row>
          <Row l="Endereço">{c.address || "—"}</Row>
          <Row l="Protocolo">{c.protocol || "—"}</Row>
          <Row l="Enviado em">{c.invite_sent_at ? new Date(c.invite_sent_at).toLocaleString("pt-BR") : "Nunca"}</Row>
          <Row l="Acessou link">{c.opened_at ? new Date(c.opened_at).toLocaleString("pt-BR") : "Não"}</Row>
          <Row l="Concluiu inscrição">{c.submitted_at ? new Date(c.submitted_at).toLocaleString("pt-BR") : "Não"}</Row>
          <Row l="Currículo">
            {c.cv_url ? (
              blobs.cv ? (
                <div className="flex items-center gap-3">
                  <a href={blobs.cv.url} download={c.cv_original_name || "curriculo.pdf"}
                     data-testid="detail-cv-download"
                     className="inline-flex items-center gap-1 bg-forest-700 text-cream-100 px-3 py-1.5 hover:bg-forest-900 text-sm">
                    <FileDown className="w-3.5 h-3.5" /> Baixar
                  </a>
                  <a href={blobs.cv.url} target="_blank" rel="noreferrer" data-testid="detail-cv-link" className="text-forest-700 underline text-sm">
                    Abrir em nova aba
                  </a>
                  <span className="text-xs text-forest-900/60">
                    {c.cv_original_name || "currículo"} ({Math.round(blobs.cv.size/1024) || "<1"} KB)
                  </span>
                </div>
              ) : errors.cv ? (
                <span className="text-terracotta-500" data-testid="detail-cv-error">Erro ao carregar: {errors.cv}</span>
              ) : <span className="text-forest-900/60">Carregando...</span>
            ) : "—"}
          </Row>
          <Row l="Selfie">
            {c.selfie_url ? (
              blobs.selfie ? (
                blobs.selfie.size < 200 ? (
                  <div className="text-terracotta-500 text-xs" data-testid="detail-selfie-invalid">
                    Arquivo inválido/corrompido ({blobs.selfie.size} bytes). Peça ao candidato para reenviar a selfie.
                    <a href={blobs.selfie.url} download={`selfie_${c.name}.jpg`} className="block mt-1 text-forest-700 underline">Baixar mesmo assim</a>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <a href={blobs.selfie.url} target="_blank" rel="noreferrer" data-testid="detail-selfie-link">
                      <img alt="selfie" src={blobs.selfie.url} data-testid="detail-selfie-img" className="w-32 h-32 object-cover border" />
                    </a>
                    <a href={blobs.selfie.url} download={`selfie_${c.name}.jpg`}
                       data-testid="detail-selfie-download"
                       className="inline-flex items-center gap-1 bg-forest-700 text-cream-100 px-3 py-1.5 hover:bg-forest-900 text-sm h-fit">
                      <FileDown className="w-3.5 h-3.5" /> Baixar
                    </a>
                  </div>
                )
              ) : errors.selfie ? (
                <span className="text-terracotta-500" data-testid="detail-selfie-error">Erro ao carregar: {errors.selfie}</span>
              ) : <span className="text-forest-900/60">Carregando imagem...</span>
            ) : "—"}
          </Row>
          <Row l="Documentos">
            {(c.docs_urls || []).length === 0 ? "—" : (
              <ul className="space-y-2" data-testid="detail-docs-list">
                {(c.docs_urls || []).map((d, i) => {
                  const key = `doc-${i}`;
                  const b = blobs[key];
                  const err = errors[key];
                  const isImage = (d.mime || "").startsWith("image/");
                  return (
                    <li key={i} className="flex items-center gap-2 flex-wrap">
                      {b ? (
                        <>
                          {isImage && b.size >= 200 && <img src={b.url} alt={d.original_name} className="w-10 h-10 object-cover border border-cream-400" />}
                          <a href={b.url} download={d.original_name}
                             data-testid={`detail-doc-download-${i}`}
                             className="inline-flex items-center gap-1 bg-forest-700 text-cream-100 px-2.5 py-1 hover:bg-forest-900 text-xs">
                            <FileDown className="w-3 h-3" /> Baixar
                          </a>
                          <a href={b.url} target="_blank" rel="noreferrer" data-testid={`detail-doc-${i}`} className="text-forest-700 underline text-sm">
                            {d.original_name}
                          </a>
                          <span className="text-xs text-forest-900/60">({Math.round(b.size/1024) || "<1"} KB)</span>
                        </>
                      ) : err ? (
                        <span className="text-terracotta-500 text-xs" data-testid={`detail-doc-error-${i}`}>
                          {d.original_name}: erro ao carregar ({err})
                        </span>
                      ) : <span className="text-forest-900/60">Carregando {d.original_name}...</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </Row>
          <Row l="IP / Dispositivo">{(c.ip_address || "—")} · <span className="text-xs">{(c.user_agent || "").slice(0, 60)}</span></Row>
        </div>
        <DialogFooter className="flex flex-wrap gap-2 mt-4">
          {c.protocol && <Button onClick={() => onDownload(c)} className="rounded-none bg-forest-700 text-cream-100 hover:bg-forest-900"><FileDown className="w-4 h-4 mr-2" /> Protocolo PDF</Button>}
          <Button onClick={() => onDecide(c.id, "aprovado")} className="rounded-none bg-forest-900 text-cream-100"><CheckCircle2 className="w-4 h-4 mr-2" /> Aprovar</Button>
          <Button onClick={() => onDecide(c.id, "indeferido")} variant="outline" className="rounded-none border-terracotta-500 text-terracotta-500"><XCircle className="w-4 h-4 mr-2" /> Indeferir</Button>
          <Button onClick={() => { onDelete(c.id); onClose(); }} variant="outline" className="rounded-none border-cream-400 ml-auto">Excluir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ l, children }) {
  return <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5 border-b border-cream-400 last:border-0">
    <div className="text-xs uppercase tracking-[0.14em] text-forest-700">{l}</div>
    <div className="text-forest-900">{children}</div>
  </div>;
}

function CandidateFormDialog({ open, onOpenChange, onSaved, processId, editing }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({ name: "", community: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) setForm({ name: editing.name || "", community: editing.community || "", phone: editing.phone_display || editing.phone || "" });
      else setForm({ name: "", community: "", phone: "" });
    }
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Nome e telefone são obrigatórios."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/secretariat/pss/candidates/${editing.id}`, form);
        toast.success("Candidato atualizado.");
      } else {
        await api.post(`/secretariat/pss/processes/${processId}/candidates`, form);
        toast.success("Candidato adicionado.");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Editar candidato" : "Adicionar candidato"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Nome completo *</span>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cand-form-name" /></label>
          <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Comunidade</span>
            <Input value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} data-testid="cand-form-community" placeholder="Ex.: Maguari, Uruará..." /></label>
          <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Telefone / WhatsApp *</span>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="cand-form-phone" placeholder="(93) 99999-9999" /></label>
          {isEdit && (
            <div className="bg-cream-200 border border-cream-400 p-3 text-xs text-forest-900/75">
              Editar CPF/RG/e-mail? Use a inscrição pública ou peça ao candidato para preencher. Este formulário atualiza apenas os dados básicos de contato.
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="cand-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              {saving ? "Salvando..." : (isEdit ? "Salvar alterações" : "Adicionar")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
